import { useNavigate, useLocation } from "react-router-dom";

interface ChallengeJoinSuccessState {
  challengeName?: string;
  challengeId?: string;
}

export function ChallengeJoinSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ChallengeJoinSuccessState | null;

  const handleViewChallenges = () => {
    navigate("/my-challenges");
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    console.log("Share clicked");
  };

  const handleClose = () => {
    if (state?.challengeId) {
      navigate(`/challenges/${state.challengeId}`, { replace: true });
    } else {
      navigate("/my-challenges", { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-between p-6 bg-background-light dark:bg-background-dark"
    >
      {/* Sticky Header with safe-area - Close Button */}
      <header className="sticky top-0 z-10 w-full flex justify-end pt-[env(safe-area-inset-top)]">
        <button
          onClick={handleClose}
          className="text-gray-500 hover:text-gray-300 transition-colors"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </header>

      {/* Main Content - Success Card */}
      <main className="flex-grow flex flex-col items-center justify-center w-full max-w-sm">
        <div className="w-full bg-card-light dark:bg-[#121212] rounded-xl card-border-gradient p-8 flex flex-col items-center text-center">
          {/* Animated Success Icon */}
          <div className="w-20 h-20 flex items-center justify-center mb-6">
            <div className="relative w-20 h-20">
              {/* Pulsing Circle 1 */}
              <div className="absolute inset-0 rounded-full bg-primary animate-circle-pulse-1"></div>
              {/* Pulsing Circle 2 */}
              <div className="absolute inset-0 rounded-full bg-primary animate-circle-pulse-2"></div>
              {/* Inner Circle with Icon */}
              <div className="absolute inset-2 rounded-full bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm flex items-center justify-center">
                <span className="material-symbols-outlined text-4xl text-primary text-glow animate-icon-pop">
                  verified
                </span>
              </div>
            </div>
          </div>

          {/* Status Text */}
          <h1 className="font-mono text-sm uppercase tracking-[0.3em] text-primary text-glow">
            Status: Accepted
          </h1>

          {/* Main Heading */}
          <p className="text-4xl font-bold tracking-tight text-content-light dark:text-white mt-3">
            Challenge Accepted
          </p>

          {/* Divider */}
          <div className="w-full border-t border-black/10 dark:border-white/10 my-8"></div>

          {/* Motivational Quote */}
          <div className="w-full text-center">
            <blockquote className="space-y-4">
              <p className="font-serif italic text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                "The secret of getting ahead is getting started."
              </p>
              <cite className="block text-right text-sm text-gray-500 not-italic">
                — Mark Twain
              </cite>
            </blockquote>
          </div>
        </div>
      </main>

      {/* Footer - Action Buttons */}
      <footer className="w-full max-w-sm pb-4 pt-8">
        <div className="space-y-4">
          {/* Primary Button - View My Challenges */}
          <button
            onClick={handleViewChallenges}
            className="block w-full text-center bg-primary text-black font-bold py-4 rounded-full text-lg hover:opacity-90 transition-opacity button-glow"
          >
            View My Challenges
          </button>

          {/* Secondary Button - Share Your Commitment */}
          <button
            onClick={handleShare}
            className="group flex items-center justify-center w-full text-center bg-transparent border border-primary/40 text-primary font-medium py-4 rounded-full text-lg hover:bg-primary/10 hover:border-primary/80 transition-all duration-300"
          >
            <span className="material-symbols-outlined mr-2 text-primary/70 group-hover:text-primary transition-colors duration-300">
              ios_share
            </span>
            Share Your Commitment
          </button>
        </div>
      </footer>

      {/* Custom Styles */}
      <style>{`
        .text-glow {
          text-shadow: 0 0 10px currentColor;
        }
        .button-glow {
          box-shadow: 0 0 16px 0 rgba(37, 244, 120, 0.5);
        }
        .card-border-gradient {
          border: 1px solid transparent;
          background: linear-gradient(#121212, #121212) padding-box,
                      linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03)) border-box;
        }

        @keyframes circle-pulse {
          0%, 100% {
            transform: scale(0.9);
            opacity: 0.1;
          }
          50% {
            transform: scale(1);
            opacity: 0.2;
          }
        }

        @keyframes icon-pop {
          0% {
            transform: scale(0.7);
            opacity: 0;
          }
          70% {
            transform: scale(1.1);
            opacity: 1;
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-circle-pulse-1 {
          animation: circle-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-circle-pulse-2 {
          animation: circle-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) 0.5s infinite;
        }

        .animate-icon-pop {
          animation: icon-pop 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      `}</style>
    </div>
  );
}
